import { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { ProductsService } from '../services/products/products.service';
import type { ProductSearchResult } from '../services/products/products.service';
import { getFirstProductImage } from '../utils/product-images';
import { ProductCard } from '../components/products/ProductCard';
import { useCartStore } from '../store/cart.store';
import { 
  ShoppingCart, 
  Heart, 
  Package, 
  Star, 
  TrendingUp, 
  Gift,
  Clock,
  Check,
  Pill,
  Sparkles,
  Zap,
  Tag,
  ArrowRight,
  User,
  ShoppingBag
} from 'lucide-react';

export const ClienteDashboardPage = () => {
  const { theme } = useTheme();
  const { addItem, getTotalItems } = useCartStore();
  const [productosDestacados, setProductosDestacados] = useState<ProductSearchResult[]>([]);
  const [productosPopulares, setProductosPopulares] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedItems, setAddedItems] = useState<string[]>([]);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    // Obtener datos del usuario
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      setUserData(JSON.parse(userDataStr));
    }
    
    loadProductos();
  }, []);

  const loadProductos = async () => {
    setLoading(true);
    try {
      // Cargar productos populares
      const popularesResponse = await ProductsService.getPopular(6);
      if (popularesResponse.success) {
        setProductosPopulares(popularesResponse.data);
      }

      // Cargar productos públicos como destacados
      const destacadosResponse = await ProductsService.getPublic(8);
      if (destacadosResponse.success) {
        setProductosDestacados(destacadosResponse.data.slice(0, 8));
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPrecioPrincipal = (producto: ProductSearchResult) => {
    return producto.precio ?? producto.presentaciones?.[0]?.precio ?? 0;
  };

  const getStockPrincipal = (producto: ProductSearchResult) => {
    return producto.stock ?? producto.presentaciones?.[0]?.stock ?? 0;
  };

  const handleAddToCart = (producto: ProductSearchResult) => {
    addItem({
      id: producto.id,
      nombre: producto.nombre,
      precio: getPrecioPrincipal(producto),
      precioAntes: undefined,
      imagen: getFirstProductImage(producto.imagenes) || getIconoCategoria(producto.categoria),
      receta: producto.requiereReceta,
      categoria: producto.categoria,
    }, true);

    // Mostrar feedback visual temporal
    setAddedItems(prev => [...prev, producto.id]);
    setTimeout(() => {
      setAddedItems(prev => prev.filter(id => id !== producto.id));
    }, 1500);
  };

  const getIconoCategoria = (categoria: string) => {
    const iconos: Record<string, string> = {
      'medicamentos': '💊',
      'cuidado-personal': '🧴',
      'higiene-femenina': '🌸',
      'salud-sexual': '💕',
      'vitaminas-y-suplementos': '🍊',
      'bebes': '👶',
      'equipos-medicos': '🏥',
    };
    return iconos[categoria] || '📦';
  };

  const promociones = [
    {
      id: 1,
      titulo: "25% OFF en Vitaminas",
      descripcion: "Fortalece tu sistema inmune con nuestras vitaminas premium",
      descuento: "25%",
      validoHasta: "31 de Marzo",
      color: "bg-gradient-to-r from-orange-500 to-red-500"
    },
    {
      id: 2,
      titulo: "Envío Gratis +$50k",
      descripcion: "Recibe tus productos en la puerta de tu casa sin costo adicional",
      descuento: "GRATIS",
      validoHasta: "Siempre",
      color: "bg-gradient-to-r from-emerald-500 to-teal-500"
    },
    {
      id: 3,
      titulo: "2x1 en Cuidado Personal",
      descripcion: "Lleva dos productos de cuidado personal y paga solo uno",
      descuento: "2x1",
      validoHasta: "15 de Abril",
      color: "bg-gradient-to-r from-purple-500 to-pink-500"
    }
  ];

  return (
    <div className={`w-full transition-colors duration-300 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Header de Bienvenida */}
      <section className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="max-w-[95%] mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                ¡Hola, {userData?.nombreCompleto || 'Cliente'}!
              </h1>
              <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Descubre nuestros productos y promociones especiales para ti
              </p>
            </div>
            
            {/* Stats rápidas */}
            <div className="flex gap-4">
              <div className={`px-4 py-3 rounded-xl ${theme === 'dark' ? 'bg-emerald-900/50' : 'bg-emerald-50'}`}>
                <div className="flex items-center space-x-2">
                  <ShoppingBag className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>En el carrito</p>
                    <p className="font-bold text-emerald-600">{getTotalItems()} productos</p>
                  </div>
                </div>
              </div>
              
              <div className={`px-4 py-3 rounded-xl ${theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-50'}`}>
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Miembro desde</p>
                    <p className="font-bold text-blue-600">
                      {userData?.creadoEn
                        ? new Date(userData.creadoEn).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })
                        : 'Recientemente'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[95%] mx-auto px-6 py-8 space-y-8">
        
        {/* Promociones Destacadas */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                🔥 Promociones Especiales
              </h2>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Ofertas exclusivas por tiempo limitado
              </p>
            </div>
            <button className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 font-medium">
              <span>Ver todas</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promociones.map((promo) => (
              <div 
                key={promo.id}
                className={`relative overflow-hidden rounded-2xl p-6 text-white ${promo.color}`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm font-semibold mb-2">
                        {promo.descuento}
                      </span>
                      <h3 className="font-bold text-lg">{promo.titulo}</h3>
                    </div>
                    <Gift className="w-6 h-6 opacity-80" />
                  </div>
                  <p className="text-white/90 text-sm mb-4">{promo.descripcion}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/70">Válido hasta {promo.validoHasta}</span>
                    <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors">
                      Ver ofertas
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Productos Populares */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold flex items-center space-x-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <span>Más Vendidos</span>
              </h2>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Los productos favoritos de nuestros clientes
              </p>
            </div>
            <button className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 font-medium">
              <span>Ver todos</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`rounded-2xl p-6 animate-pulse ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="h-20 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded mb-4"></div>
                  <div className="h-10 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4 items-stretch">
              {productosPopulares.map((producto) => (
                <ProductCard
                  key={producto.id}
                  producto={producto}
                  theme={theme}
                  added={addedItems.includes(producto.id)}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </section>

        {/* Productos Destacados */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl font-bold flex items-center space-x-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                <Sparkles className="w-6 h-6 text-purple-600" />
                <span>Productos Destacados</span>
              </h2>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Selección especial de nuestros mejores productos
              </p>
            </div>
            <button className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 font-medium">
              <span>Explorar catálogo</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`rounded-xl p-4 animate-pulse ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="h-16 bg-gray-300 rounded mb-3"></div>
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-8 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4 items-stretch">
              {productosDestacados.map((producto) => (
                <ProductCard
                  key={producto.id}
                  producto={producto}
                  theme={theme}
                  added={addedItems.includes(producto.id)}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </section>

        {/* CTA para explorar más */}
        <section className={`rounded-2xl p-8 text-center ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border border-emerald-500/20' 
            : 'bg-gradient-to-r from-emerald-500 to-teal-600'
        }`}>
          <div className="max-w-2xl mx-auto">
            
            <h3 className="text-2xl font-bold text-white mb-3">
              ¿No encuentras lo que buscas?
            </h3>
            <p className="text-white/90 mb-6">
              Explora nuestro catálogo completo con más de 10,000 productos de salud y bienestar
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button className="px-6 py-3 bg-white text-emerald-700 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg">
                Ver todos los productos
              </button>
              <button className="px-6 py-3 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300">
                Contactar asesor
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ClienteDashboardPage;